import React, {memo, useState} from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CircularProgress from "@mui/material/CircularProgress";

export default function CustomerStrategy({ customerSelectionStrategySuggestions, loading, showNoSuggestionsIndicator, error }) {
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (key) => {
        setExpandedRows((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };
    return (
        <Box mt={{ xs: "14px", sm: "20px" }}>
            <Box component="h1" sx={styles.headingLarge}>
                Customer Selection Strategy
            </Box>

            <Box component={Paper} sx={styles.paper}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={[styles.tableCell, styles.summaryCol]}>
                                    Summary
                                </TableCell>
                                <TableCell sx={[styles.tableCell, styles.descriptionCol]}>
                                    Description
                                </TableCell>
                                <TableCell
                                    sx={[styles.tableCell, styles.countCol]}
                                    align="right"
                                >
                                    Count
                                </TableCell>
                                <TableCell sx={[styles.tableCell, styles.expandCol]} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell sx={styles.tableCell}>
                                         <CircularProgress sx={{color: "#003300"}}  size={24}/>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && showNoSuggestionsIndicator && (
                                <TableRow>
                                    <TableCell sx={styles.tableCell} colSpan={6}>
                                        Customer selection strategy not available
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && !showNoSuggestionsIndicator && error && (
                                <TableRow>
                                    <TableCell sx={styles.tableCell} colSpan={6}>
                                        Failed to load customer selection strategy
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading  && !showNoSuggestionsIndicator && !error && customerSelectionStrategySuggestions?.map((row, idx) => {
                                const key = `${row?.name ?? "row"}-${idx}`;

                                return (
                                    <StrategyRow
                                        key={key}
                                        row={row}
                                        rowKey={key}
                                        isExpanded={!!expandedRows[key]}
                                        onToggle={toggleRow}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}

const StrategyRow = memo(function StrategyRow({row, rowKey, isExpanded, onToggle}) {
    const queryText = row?.extraFields?.query;
    const hasQuery = Boolean(queryText);

    return (
        <>
            <TableRow>
                <TableCell
                    sx={[
                        styles.tableCell,
                        styles.summaryCol,
                        isExpanded && styles.tableCellExpanded,
                    ]}
                >
                    <Box sx={styles.noEllipsis}>{row?.name ?? "-"}</Box>
                </TableCell>

                <TableCell sx={[styles.tableCell, styles.descriptionCol, isExpanded && styles.tableCellExpanded]}>
                    <Box sx={styles.noEllipsis}>{row?.description ?? "-"}</Box>
                </TableCell>

                <TableCell
                    sx={[styles.tableCell, styles.countCol, isExpanded && styles.tableCellExpanded]}
                    align="right"
                >
                    <Box sx={styles.noEllipsis}>{row?.count ?? "-"}</Box>
                </TableCell>

                <TableCell
                    sx={[styles.tableCell, styles.expandCol, isExpanded && styles.tableCellExpanded]}
                    align="right"
                >
                    {hasQuery && (
                        <IconButton
                            onClick={() => onToggle(rowKey)}
                            size="small"
                            sx={styles.expandButton}
                            aria-label={isExpanded ? "Hide query" : "Show query"}
                        >
                            {isExpanded ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                            )}
                        </IconButton>
                    )}
                </TableCell>
            </TableRow>

            {hasQuery && (
                <TableRow>
                    <TableCell sx={[styles.queryCell, isExpanded && styles.queryCellExpanded]} colSpan={4}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={styles.queryBox}>
                                Shopify Query
                                <Box component="code" sx={styles.queryCode}>
                                    {queryText}
                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
});


export const styles = {
    root: {
        borderRadius: "10px",
        border: "1px solid #B6BEBF98",
        background: "#fff",
    },
    paper: {
        borderRadius: "8px",
        border: "1px solid #CED4D4",
        boxShadow:
            "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 0.5px, rgba(0, 0, 0, 0.1) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 0px 0px 0px",
    },
    headingLarge: {
        m: 0,
        fontFamily: "LarsseitMedium",
        fontSize: 24,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
        color: "#0C2728",
        mb: { xs: "24px", sm: "24px" },
    },
    header: {
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 0 },
        minHeight: { xs: "auto", md: "73px" },
        zIndex: 10,
    },
    tableCell: {
        fontFamily: "FustatSemiBold",
        borderBottom: "1px solid #B6BEBF98",
        fontSize: "14px",
        fontWeight: 500,
        color: "#302E2A",
        paddingTop: "16px",
        paddingBottom: "16px",
        "&:first-of-type": {
            paddingLeft: "30px",
        },
        "&:last-of-type": {
            paddingRight: "30px",
        },
    },
    tableCellExpanded: {
        borderBottom: "none",
    },
    noEllipsis: {
        whiteSpace: "normal",
        overflowWrap: "break-word",
        wordBreak: "break-word",
    },
    summaryCol: {
        maxWidth: 220,
    },
    descriptionCol: {
        maxWidth: 420,
    },
    countCol: {
        maxWidth: 80,
    },
    expandCol: {
        maxWidth: 60,
    },
    queryCell: {
        paddingTop: 0,
        paddingBottom: 0,
        borderBottom: "none",
    },
    queryCellExpanded: {
        borderBottom: "1px solid #B6BEBF98",
    },
    queryBox: {
        marginTop: 0,
        marginBottom: "8px",
        padding: "8px 12px",
        backgroundColor: "#F7F7F2",
        borderRadius: "6px",
        border: "1px solid #D0D0C8",
        fontFamily: "FustatMedium",
        fontSize: "14px",
        textTransform: "uppercase",
    },
    queryCode: {
        display: "block",
        fontFamily: "monospace",
        fontSize: "13px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        textTransform: "none",
    },
    expandButton: {
        padding: 0,
    },
};
